import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  CircularLoadingButton,
  ConfirmationDialog,
  ConfirmationDialogAction,
  ConfirmationDialogCancel,
  ConfirmationDialogContent,
  ConfirmationDialogDescription,
  ConfirmationDialogFooter,
  ConfirmationDialogHeader,
  ConfirmationDialogOverlay,
  ConfirmationDialogPortal,
  ConfirmationDialogRoot,
  ConfirmationDialogTitle,
} from '@mint-lib/tw-components';

import useChangeStatusMutation from '../../api/mutations/useChangeStatusMutation.js';
import { useDisableFlowModalContext } from '../../atoms/disableFlowModal.js';

const DisableFlowModal = () => {
  const { t } = useTranslation('@myn/approval_v2');

  const [modalContext, setModalContext] = useDisableFlowModalContext();
  const { changeFlowStatus, isPending } = useChangeStatusMutation();

  const handleClose = () => {
    setModalContext(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    handleClose();
  };

  const handleSubmit = async () => {
    if (!modalContext) {
      handleClose();
      return;
    }

    try {
      await changeFlowStatus({
        flowId: modalContext.flowId,
        isActive: false,
      });

      enqueueSnackbar({
        title: t('Flow disabled successfully.'),
        variant: 'success',
        removable: true,
      });
    } catch {
      enqueueSnackbar({
        title: t('Flow was not disabled.'),
        variant: 'error',
        removable: true,
      });
    }
  };

  return (
    <ConfirmationDialog
      open={modalContext?.isOpened}
      onOpenChange={handleOpenChange}
    >
      <ConfirmationDialogPortal>
        <ConfirmationDialogOverlay onClick={handleClose} />
        <ConfirmationDialogRoot>
          <ConfirmationDialogHeader onClose={handleClose}>
            <WarningAltFilled className="size-4 fill-warning-main" />
            <ConfirmationDialogTitle>
              {t('Disable this approval flow?')}
            </ConfirmationDialogTitle>
          </ConfirmationDialogHeader>
          <ConfirmationDialogContent>
            <ConfirmationDialogDescription>
              <span className="block">
                {t(
                  'Disabling this flow will immediately withdraw all pending approval requests.',
                )}
              </span>
              <span className="block">
                {t('All participants will be notified.')}
              </span>
            </ConfirmationDialogDescription>
          </ConfirmationDialogContent>
          <ConfirmationDialogFooter>
            <ConfirmationDialogCancel onClick={handleClose} />
            <ConfirmationDialogAction
              color="warning"
              onClick={handleSubmit}
              disabled={isPending}
            >
              {isPending && (
                <CircularLoadingButton size="small" variant="contained" />
              )}
              {t('Yes, disable')}
            </ConfirmationDialogAction>
          </ConfirmationDialogFooter>
        </ConfirmationDialogRoot>
      </ConfirmationDialogPortal>
    </ConfirmationDialog>
  );
};

export default DisableFlowModal;
